import { useEffect, useState } from 'react';
import { Images, Newspaper, Users, Mail, Send, Sparkles, UserPlus, AlertTriangle, CalendarCheck, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAdminStats } from '@/lib/api';
import { ADMIN_DASHBOARD } from '@/constants/testIds';

const STAT_DEFS = [
  { key: 'upcoming_sessions', label: 'Sessions Next 14 Days', icon: CalendarCheck },
  { key: 'new_leads', label: 'New Leads', icon: UserPlus },
  { key: 'blueprints', label: 'Creative Blueprints', icon: Sparkles },
  { key: 'galleries', label: 'Galleries', icon: Images },
  { key: 'journal_posts', label: 'Journal Posts', icon: Newspaper, ownerOnly: true },
  { key: 'clients', label: 'Client Accounts', icon: Users },
  { key: 'contact_messages', label: 'Contact Messages', icon: Mail, ownerOnly: true },
  { key: 'newsletter_subscribers', label: 'Newsletter Subscribers', icon: Send, ownerOnly: true },
];

export const AdminOverviewTab = ({ onNavigateToGalleries, onNavigateToBookings }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getAdminStats().then(setStats).catch(() => setStats({}));
  }, []);

  const openRevisions = stats?.open_revision_requests || 0;
  const requested = stats?.requested_bookings || 0;

  return (
    <div>
      {requested > 0 && (
        <div
          data-testid="admin-booking-requests-banner"
          className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[hsl(var(--lxra-gold))]/40 bg-[hsl(var(--lxra-gold))]/[0.06] px-5 py-4"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] shrink-0">
              <Inbox className="h-4 w-4" />
            </span>
            <p className="text-sm text-[hsl(var(--lxra-fg))]">
              <strong className="text-[hsl(var(--lxra-gold))]">{requested}</strong> booking request{requested === 1 ? '' : 's'} waiting for a confirmed date.
            </p>
          </div>
          <Button
            onClick={onNavigateToBookings}
            data-testid="admin-booking-requests-review-button"
            variant="outline"
            className="h-9 px-4 border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 hover:text-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.08em] font-semibold"
          >
            Review Requests
          </Button>
        </div>
      )}
      {openRevisions > 0 && (
        <div
          data-testid={ADMIN_DASHBOARD.revisionAlertBanner}
          className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/[0.06] px-5 py-4"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-400/40 text-amber-400 shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <p className="text-sm text-[hsl(var(--lxra-fg))]">
              <strong className="text-amber-400">{openRevisions}</strong> client{openRevisions === 1 ? '' : 's'} requested a photo revision that hasn&apos;t been reviewed yet.
            </p>
          </div>
          <Button
            onClick={onNavigateToGalleries}
            data-testid={ADMIN_DASHBOARD.revisionAlertReviewButton}
            variant="outline"
            className="h-9 px-4 border-amber-400/40 text-amber-400 hover:bg-amber-400/10 hover:text-amber-300 text-xs uppercase tracking-[0.08em] font-semibold"
          >
            Review Now
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-overview-stats">
        {STAT_DEFS.filter((d) => !d.ownerOnly || stats?.is_owner).map(({ key, label, icon: Icon }) => (
          <div key={key} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6" data-testid={`admin-stat-${key}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/30 text-[hsl(var(--lxra-gold))]">
                <Icon className="h-5 w-5" />
              </span>
              <span className="font-display text-3xl text-[hsl(var(--lxra-fg))]">
                {stats ? (stats[key] ?? 0) : '—'}
              </span>
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
